/**
 * Secure Logger Utility
 * 
 * Prevents accidental logging of sensitive data like:
 * - JWT tokens
 * - Session data
 * - Passwords
 * - API keys
 * - User credentials
 */

// Patterns that indicate sensitive data
const SENSITIVE_PATTERNS = [
  /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, // JWT tokens
  /Bearer\s+[A-Za-z0-9_.-]+/gi, // Bearer tokens
  /password["\s:=]+["']?[^"'\s,}]+/gi, // Password fields
  /secret["\s:=]+["']?[^"'\s,}]+/gi, // Secret fields
  /api[_-]?key["\s:=]+["']?[^"'\s,}]+/gi, // API keys
  /access[_-]?token["\s:=]+["']?[^"'\s,}]+/gi, // Access tokens
  /refresh[_-]?token["\s:=]+["']?[^"'\s,}]+/gi, // Refresh tokens
  /authorization["\s:=]+["']?[^"'\s,}]+/gi, // Authorization headers
];

// Keys that should never be logged
const SENSITIVE_KEYS = new Set([
  'password',
  'secret',
  'token',
  'access_token',
  'refresh_token',
  'accessToken',
  'refreshToken',
  'apiKey',
  'api_key',
  'authorization',
  'auth',
  'session',
  'jwt',
  'credential',
  'credentials',
  'privateKey',
  'private_key',
]);

/**
 * Sanitizes a string by redacting sensitive patterns
 */
function sanitizeString(str: string): string {
  let sanitized = str;
  
  for (const pattern of SENSITIVE_PATTERNS) {
    sanitized = sanitized.replace(pattern, '[REDACTED]');
  }
  
  return sanitized;
}

/**
 * Deep sanitizes an object, redacting sensitive keys and values
 */
function sanitizeObject(obj: unknown, depth = 0): unknown {
  // Prevent infinite recursion
  if (depth > 10) return '[MAX_DEPTH]';
  
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (typeof obj === 'string') {
    return sanitizeString(obj);
  }
  
  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, depth + 1));
  }
  
  if (typeof obj === 'object') {
    const sanitized: Record<string, unknown> = {};
    
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();
      
      // Redact sensitive keys entirely
      if (SENSITIVE_KEYS.has(lowerKey) || 
          lowerKey.includes('password') ||
          lowerKey.includes('secret') ||
          lowerKey.includes('token') ||
          lowerKey.includes('key') && lowerKey.includes('api')) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeObject(value, depth + 1);
      }
    }
    
    return sanitized;
  }
  
  return String(obj);
}

/**
 * Production-safe logger that sanitizes sensitive data
 */
export const secureLog = {
  /**
   * Log info level (only in development by default)
   */
  info: (...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.log(...args.map(arg => sanitizeObject(arg)));
    }
  },
  
  /**
   * Log warning level
   */
  warn: (...args: unknown[]) => {
    console.warn(...args.map(arg => sanitizeObject(arg)));
  },
  
  /**
   * Log error level (always logs, but sanitized)
   */
  error: (...args: unknown[]) => {
    console.error(...args.map(arg => sanitizeObject(arg)));
  },
  
  /**
   * Debug log (development only)
   */
  debug: (...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.debug(...args.map(arg => sanitizeObject(arg)));
    }
  },
  
  /**
   * Sanitize data for logging without actually logging
   */
  sanitize: (data: unknown): unknown => {
    return sanitizeObject(data);
  },
};

/**
 * Check if running in production
 */
export function isProduction(): boolean {
  return import.meta.env.PROD;
}

/**
 * Disable console methods in production (except errors)
 * Call this at app startup
 */
export function enforceProductionLogging(): void {
  if (isProduction()) {
    // Keep console.error but sanitize it
    const originalError = console.error;
    console.error = (...args: unknown[]) => {
      originalError(...args.map(arg => sanitizeObject(arg)));
    };
    
    // Disable info-level logs in production
    console.log = () => {};
    console.debug = () => {};
    console.info = () => {};
    
    // Keep warn but sanitize
    const originalWarn = console.warn;
    console.warn = (...args: unknown[]) => {
      originalWarn(...args.map(arg => sanitizeObject(arg)));
    };
  }
}

export default secureLog;
